import axios, { AxiosInstance } from 'axios';
import Toast from '@/classes/SnotifyConfiguration';
import router from '@/router';
import { HttpResponseCodeConstants, MStoreExceptionResponseModel, FluentModelValidationErrorModel, ErrorReponseConstants, NotificationConstants, NotificationPresentationType } from '@/types';

export default class AxiosConfiguration {
  public static axiosWithoutNotification: AxiosInstance = {} as AxiosInstance;
  public static axiosWithErrorPage: AxiosInstance = {} as AxiosInstance;

  public static configure() {
    this.configureDefaultInstance();
    this.configureWithoutNotificationInstance();
    this.configureErrorPageInstance();
  }

  private static axiosNotificationResponseInterceptor: any = null;
  private static axiosErrorPageResponseInterceptor: any = null;
  private static axiosWithoutNotificationResponseInterceptor: any = null;
  private static errorMessage = '';

  private static addNotificationInterceptor() {
    if (this.axiosNotificationResponseInterceptor) {
      return;
    }

    this.axiosNotificationResponseInterceptor = axios.interceptors.response.use(
      (response) => {
        if (response.data !== null && response.data.hasNotification && response.data.showToast) {
          Toast.showInformation(response.data.notification);
        }

        return response;
      },
      (error) => {
        if (this.isBlobErrorResponse(error)) {
          return Promise.reject(error);
        }
        if (this.handleErrorResponse(error)) {
          Toast.showExceptionNotification(this.makeExceptionResponseModel(error));
        }

        return Promise.reject(this.makeExceptionResponseModel(error));
      }
    );
  }

  private static addErrorPageInterceptor(this: any) {
    const vm = this;
    if (this.axiosErrorPageResponseInterceptor) {
      return;
    }

    this.axiosErrorPageResponseInterceptor = this.axiosWithErrorPage.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        if (this.isBlobErrorResponse(error)) {
          return Promise.reject(error);
        }
        if (this.handleErrorResponse(error)) {
          const model: MStoreExceptionResponseModel = this.makeExceptionResponseModel(error);
          router.push({
            name: 'errorPage',
            params: {
              errorCode: error.response.status.toString(),
              errorMessage: this.getExceptionNotification(error),
              notificationPresentationType: model.presentationType.toString()
            }
          });
        }

        return Promise.reject(this.makeExceptionResponseModel(error));
      }
    );
  }

  private static addWithoutNotificationInterceptor(this: any) {
    if (this.axiosWithoutNotificationResponseInterceptor) {
      return;
    }

    this.axiosWithoutNotificationResponseInterceptor = this.axiosWithoutNotification.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        if (this.isBlobErrorResponse(error)) {
          return Promise.reject(error);
        }
        return Promise.reject(this.makeExceptionResponseModel(error));
      }
    );
  }

  private static configureDefaultInstance() {
    this.addAxiosDefaults(axios);
    this.addNotificationInterceptor();
  }

  private static configureWithoutNotificationInstance() {
    this.axiosWithoutNotification = axios.create();
    this.addAxiosDefaults(this.axiosWithoutNotification);
    this.addWithoutNotificationInterceptor();
  }

  private static configureErrorPageInstance() {
    this.axiosWithErrorPage = axios.create();
    this.addAxiosDefaults(this.axiosWithErrorPage);
    this.addErrorPageInterceptor();
  }

  private static addAxiosDefaults(axiosInstance: AxiosInstance) {
    axiosInstance.defaults.headers.get.Pragma = 'no-cache';
    axiosInstance.defaults.baseURL = `${process.env.BASE_URL}api/`;
  }

  private static handleErrorResponse(error: any): boolean {
    this.errorMessage = NotificationConstants.UnknownError;
    if (HttpResponseCodeConstants.handledHttpResponseCodes.includes(error.response.status)) {
      this.errorMessage = this.getExceptionNotification(error);
      return true;
    }

    if (error.response.status === 503) {
      window.location.href = '/';
    }
    return false;
  }

  private static makeExceptionResponseModel(this: any, error: any): MStoreExceptionResponseModel {
    const errorResponseData = error.response.data;
    if (this.isMStoreExceptionResponse(errorResponseData)) {
      return errorResponseData;
    }

    if (this.isFluentModelValidationError(errorResponseData)) {
      return {
        status: errorResponseData.status,
        notification: errorResponseData.title,
        presentationType: NotificationPresentationType.UnexpectedError,
        reference: null
      };
    }

    return ErrorReponseConstants.DefaultMStoreExceptionResponseModel;
  }

  private static getExceptionNotification(this: any, error: any): string {
    const exceptionResponseModel: MStoreExceptionResponseModel = this.makeExceptionResponseModel(error);
    return exceptionResponseModel.notification;
  }

  private static isMStoreExceptionResponse(error: any): error is MStoreExceptionResponseModel {
    return 'presentationType' in error;
  }

  private static isFluentModelValidationError(error: any): error is FluentModelValidationErrorModel {
    return 'traceId' in error;
  }

  private static isBlobErrorResponse(error: any): boolean {
    return error?.response?.config?.responseType === 'blob';
  }
}
